import { AxiosError } from 'axios';
import { axiosInstance } from '../api/axios.instance';
import { API_CONFIG } from '../api/config';
import { 
  ListarLicitacionesResponseSchema,
  type Licitacion 
} from '../schemas/licitaciones/listar-licitaciones/api.schema';
import { type ListarLicitacionesUi as ListarLicitacionesNuevaUi } from '../schemas/licitaciones/listar-licitaciones/ui.schema';
import { 
  LicitacionDetalleResponseSchema,
  transformLicitacionDetalleApiToUi,
  type LicitacionDetalleUI
} from '../schemas/licitaciones/detalle';
import {
  ListarMensajesLicitacionesResponseSchema,
  transformMensajesLicitacionesApiToUi
} from '../schemas/licitaciones/listar-mensajes';
import { type ListarMensajesLicitacionesUi } from '../schemas/licitaciones/listar-mensajes';
import {
  CrearMensajeLicitacionRequestSchema,
  CrearMensajeLicitacionResponseSchema,
  type CrearMensajeLicitacionResponse
} from '../schemas/licitaciones/crear-mensaje';

export class LicitacionesService {

  static async listarLicitaciones(
    page: number = 1,
    limit: number = 20,
    filters?: {
      estado?: string;
      search?: string;
      estado_interno?: string;
      codigo_estado?: number;
      postulado?: boolean;
      adjudicado?: boolean;
      no_leidas?: boolean;
      fecha_cierre_desde?: string;
      fecha_cierre_hasta?: string;
      fecha_publicacion_desde?: string;
      fecha_publicacion_hasta?: string;
    }
  ): Promise<{
    data: ListarLicitacionesNuevaUi[];
    total: number;
    page: number;
    limit: number;
  }> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (filters?.estado) {
        params.append('estado', filters.estado);
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }
      if (filters?.estado_interno) {
        params.append('estado_interno', filters.estado_interno);
      }
      if (filters?.codigo_estado !== undefined && filters?.codigo_estado !== null) {
        params.append('codigo_estado', filters.codigo_estado.toString());
      }
      if (filters?.postulado !== undefined && filters?.postulado !== null) {
        params.append('postulado', filters.postulado.toString());
      }
      if (filters?.adjudicado !== undefined && filters?.adjudicado !== null) {
        params.append('adjudicado', filters.adjudicado.toString());
      }
      if (filters?.no_leidas !== undefined && filters?.no_leidas !== null) {
        params.append('no_leidas', filters.no_leidas.toString());
      }
      if (filters?.fecha_cierre_desde) {
        params.append('fecha_cierre_desde', filters.fecha_cierre_desde);
      }
      if (filters?.fecha_cierre_hasta) {
        params.append('fecha_cierre_hasta', filters.fecha_cierre_hasta);
      }
      if (filters?.fecha_publicacion_desde) {
        params.append('fecha_publicacion_desde', filters.fecha_publicacion_desde);
      }
      if (filters?.fecha_publicacion_hasta) {
        params.append('fecha_publicacion_hasta', filters.fecha_publicacion_hasta);
      }

      const response = await axiosInstance.get(
        `${API_CONFIG.ENDPOINTS.LICITACIONES.LISTAR_LICITACIONES}?${params.toString()}`
      );
      
      const validatedData = ListarLicitacionesResponseSchema.parse(response.data);
      
      const transformedData = validatedData.data.map((item: Licitacion) => ({
        id: item.data.id,
        nombre: item.data.nombre,
        resumen: item.data.resumen,
        codigo_externo: item.data.codigo_externo,
        codigo_estado: item.data.codigo_estado,
        estado_licitacion: item.data.estado_licitacion,
        fecha_creacion: item.data.fecha_creacion,
        fecha_cierre: item.data.fecha_cierre,
        archivos: item.archivos,
        visto: item.data.visto,
        postulado: item.data.postulado,
        fecha_postulacion: item.data.fecha_postulacion,
        adjudicado: item.data.adjudicado,
        fecha_adjudicacion: item.data.fecha_adjudicacion
      }));

      return {
        data: transformedData,
        total: validatedData.total,
        page: validatedData.page,
        limit: validatedData.limit
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada o no autorizada');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      console.error('❗ Error inesperado al listar licitaciones:', error);
      throw new Error('Error desconocido al obtener las licitaciones');
    }
  }

  static async fetchLicitacionDetail(codigoExterno: string): Promise<LicitacionDetalleUI> {
    try {
      const response = await axiosInstance.get(
        API_CONFIG.ENDPOINTS.LICITACIONES.DETALLE(codigoExterno)
      );
      
      const validatedData = LicitacionDetalleResponseSchema.parse(response.data);
      
      return transformLicitacionDetalleApiToUi(validatedData);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada o no autorizada');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al obtener el detalle de la licitación');
    }
  }

  static async marcarComoVisto(licitacionId: number): Promise<void> {
    try {
      await axiosInstance.post(
        API_CONFIG.ENDPOINTS.LICITACIONES.MARCAR_VISTO(licitacionId)
      );
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('Sesión expirada o no autorizada');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      console.error('❗ Error inesperado al marcar licitación como vista:', error);
      throw new Error('Error desconocido al marcar la licitación como vista');
    }
  }

  static async postular(licitacionId: string | number): Promise<{ message: string; data: any }> {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.LICITACIONES.POSTULAR(licitacionId));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al postular la licitación');
    }
  }

  static async adjudicar(licitacionId: string | number): Promise<{ message: string; data: any }> {
    try {
      const response = await axiosInstance.post(API_CONFIG.ENDPOINTS.LICITACIONES.ADJUDICAR(licitacionId));
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al adjudicar la licitación');
    }
  }

  static async listarMensajes(licitacionId: number): Promise<ListarMensajesLicitacionesUi> {
    try {
      const response = await axiosInstance.get(
        API_CONFIG.ENDPOINTS.LICITACIONES.MENSAJES(licitacionId)
      );
      
      const validatedData = ListarMensajesLicitacionesResponseSchema.parse(response.data);
      
      return transformMensajesLicitacionesApiToUi(validatedData);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al obtener los mensajes de la licitación');
    }
  }

  static async crearMensaje(licitacionId: number, mensaje: string): Promise<CrearMensajeLicitacionResponse> {
    try {
      const requestData = CrearMensajeLicitacionRequestSchema.parse({ mensaje });
      
      const response = await axiosInstance.post(
        API_CONFIG.ENDPOINTS.LICITACIONES.CREAR_MENSAJE(licitacionId),
        requestData
      );
      
      return CrearMensajeLicitacionResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al crear el mensaje');
    }
  }

  static async eliminarMensaje(mensajeId: string): Promise<void> {
    try {
      await axiosInstance.delete(API_CONFIG.ENDPOINTS.LICITACIONES.ELIMINAR_MENSAJE(mensajeId));
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al eliminar el mensaje');
    }
  }

  static async obtenerLinkDescarga(archivoId: number): Promise<string> {
    try {
      const response = await axiosInstance.get<{ url: string }>(
        API_CONFIG.ENDPOINTS.LICITACIONES.DESCARGAR_ARCHIVO_LINK(archivoId)
      );
      return response.data.url;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al obtener el link de descarga');
    }
  }
  
  static async subirAdjuntosManual(codigoExterno: string, formData: FormData): Promise<{ message: string; data: any }> {
    try {
      const response = await axiosInstance.post(
        API_CONFIG.ENDPOINTS.LICITACIONES.SUBIR_ADJUNTOS_MANUAL(codigoExterno),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al subir los archivos adjuntos');
    }
  }

  static async analizarDocumentos(codigoExterno: string, tematicaId?: number): Promise<{ message: string; data: any }> {
    console.log("🚀 [LicitacionesService] Enviando petición con Temática ID:", tematicaId);
    try {
      const response = await axiosInstance.post(
        API_CONFIG.ENDPOINTS.LICITACIONES.ANALIZAR_DOCUMENTOS(codigoExterno),
        null, // No mandamos body, va por query params
        {
          params: tematicaId ? { tematica_id: tematicaId } : {}
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error('No autorizado: Verifica que estés autenticado');
        }
        throw new Error(error.response?.data?.detail || error.message);
      }
      throw new Error('Error desconocido al analizar los documentos');
    }
  }

} 